// UniversalLinksBootstrap — handles iOS Universal Links + custom-scheme deeplinks.
//
// When a user taps an `https://mywyngx.com/...` link from outside the app
// (e.g. a magic-link email, a push notification, a shared chat invite), iOS
// fires Capacitor's `App.appUrlOpen` event with the FULL URL. We extract the
// pathname (e.g. `/auth/magic/abc123`) and hand it to React Router so the
// already-rendered SPA can take over — no full reload, no WebView restart.
//
// Mounted ABOVE the auth gate so it fires even when the user isn't logged in
// yet (magic-link login = quintessential first-tap-after-install flow).
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { diagLog } from "@/lib/diagnostics";

// Paths the SPA owns. Anything outside this set falls back to a full
// `window.location.assign` so the React Router error-boundary doesn't
// swallow legitimate web URLs (e.g. a marketing page added later).
const SPA_PREFIXES = [
    "/auth/magic/",
    "/auth/callback",
    "/dashboard",
    "/onboarding",
    "/plan",
    "/chats",
    "/chat/",
    "/challenges",
    "/challenge/",
    "/friends",
    "/friends/accept",
    "/referrals",
    "/referral/",
    "/event/",
    "/profile/",
    "/coach",
    "/social",
    "/settings",
    "/account",
    "/badges",
    "/crews",
    "/shadow",
    "/twin",
    "/hero",
    "/nutrition",
    "/grocery",
    "/integrations",
    "/login",
    "/register",
    "/promo",
];

function isSpaPath(pathname) {
    return SPA_PREFIXES.some((p) =>
        pathname === p.replace(/\/$/, "") || pathname.startsWith(p));
}

// Module-level state — survives component remounts. Critical on iOS
// where `getLaunchUrl()` always returns the original launch URL for
// the entire app process lifetime. Without this guard, every remount
// of UniversalLinksBootstrap would re-route the user back to the
// Universal Link path, creating an infinite redirect loop with
// MagicConsume → /dashboard → UL remount → /auth/magic → ...
//
// `coldLaunchProcessed` flips true once we've handled the launch URL
// once per app session. We MIRROR the flag into `sessionStorage` so
// it also survives WKWebView page reloads (pull-to-refresh on iOS
// reloads the JS context but reuses the same App process, which
// means `App.getLaunchUrl()` STILL returns the original magic-link
// URL and a module-level flag wouldn't help). sessionStorage is
// cleared when the app is genuinely backgrounded → re-launched
// from a fresh launch URL, so legitimate new magic-link taps still
// route correctly.
//
// `seen` keeps a short-lived dedupe window for warm-launches so the
// same `appUrlOpen` event firing twice in quick succession (which
// iOS does) only routes once.
const COLD_LAUNCH_FLAG = "wyng.ul.coldLaunchProcessed";
let coldLaunchProcessed =
    (typeof sessionStorage !== "undefined" &&
     sessionStorage.getItem(COLD_LAUNCH_FLAG) === "1");
const seen = new Set();

export default function UniversalLinksBootstrap() {
    const navigate = useNavigate();

    useEffect(() => {
        if (Capacitor.getPlatform() !== "ios") return;

        // Per-call dedup using module-level `seen` (survives remounts).
        const tryRoute = (url) => {
            if (!url || seen.has(url)) return;
            seen.add(url);
            setTimeout(() => seen.delete(url), 5000);
            routeFromUrl(url, navigate);
        };

        let sub = null;
        (async () => {
            try {
                const { App } = await import("@capacitor/app");
                diagLog("info", "ul", "bootstrap mounted, listening");

                // 1) Cold-launch ONLY runs once per app session. Module-
                //    level `coldLaunchProcessed` flag prevents the remount
                //    loop where every re-render of this component would
                //    re-route back to the original launch URL.
                if (!coldLaunchProcessed) {
                    coldLaunchProcessed = true;
                    try { sessionStorage.setItem(COLD_LAUNCH_FLAG, "1"); }
                    catch { /* private mode — module-level flag still works */ }
                    try {
                        const launch = await App.getLaunchUrl();
                        if (launch?.url) {
                            diagLog("info", "ul",
                                `getLaunchUrl: ${launch.url}`);
                            // Extra safety net: if the launch URL is a
                            // magic-link AND the user is already signed
                            // in (access token persisted from a previous
                            // session), don't re-consume — the token has
                            // a 100% chance of being already used and we
                            // would otherwise route the user to a bare
                            // /login page after pull-to-refresh.
                            if (await _shouldSkipMagicReplay(launch.url)) {
                                diagLog("info", "ul",
                                    "skip magic-link replay (already signed in)");
                            } else {
                                tryRoute(launch.url);
                            }
                        } else {
                            diagLog("info", "ul", "getLaunchUrl: (none)");
                        }
                    } catch (e) {
                        diagLog("warn", "ul",
                            `getLaunchUrl threw: ${e?.message || e}`);
                    }
                } else {
                    diagLog("info", "ul",
                        "cold-launch already processed; skipping getLaunchUrl");
                }

                // 2) Warm-launch: app already running, iOS hands us a new URL.
                sub = await App.addListener("appUrlOpen", (event) => {
                    diagLog("info", "ul",
                        `appUrlOpen: ${event?.url || "(no url)"}`);
                    if (event?.url) tryRoute(event.url);
                });
            } catch (e) {
                diagLog("error", "ul",
                    `bootstrap failed: ${e?.message || e}`,
                    e?.stack || null);
            }
        })();

        return () => {
            try { sub?.remove?.(); } catch { /* noop */ }
        };
    }, [navigate]);

    return null;
}

// Returns true when `rawUrl` is a magic-link landing URL AND the user
// already has an access token persisted from a previous session. In
// that case the magic-link token is guaranteed to be either (a) the
// SAME one that gave us the persisted token (consumed already, so
// re-consuming will 400) or (b) a stale one from before — neither
// of which we want to push the user through. Returning true makes
// the caller short-circuit so the app naturally lands the user on
// /dashboard via the existing ProtectedRoute → /auth/me flow.
async function _shouldSkipMagicReplay(rawUrl) {
    try {
        if (!rawUrl.includes("/auth/magic/")) return false;
        const { loadTokens } = await import("@/lib/tokenStore");
        const t = await loadTokens();
        return !!(t && (t.access || t.refresh));
    } catch {
        return false;
    }
}

function routeFromUrl(rawUrl, navigate) {
    try {
        const u = new URL(rawUrl);
        // Sanity check: only handle our own domains.
        const host = u.hostname.toLowerCase();
        const ours = host === "mywyngx.com"
                  || host === "app.mywyngx.com"
                  || host.endsWith(".mywyngx.com");
        if (!ours) {
            // Custom-scheme fallback (e.g. `wyng://magic/abc`) — Capacitor
            // can also fire `appUrlOpen` for those; treat path as the SPA
            // route directly.
            if (rawUrl.startsWith("wyng://")) {
                const path = "/" + rawUrl.replace(/^wyng:\/\//, "");
                navigate(path, { replace: true });
            }
            return;
        }

        const target = u.pathname + u.search + u.hash;
        diagLog("info", "ul", `route → ${target}`);
        if (isSpaPath(u.pathname)) {
            navigate(target, { replace: true });
        } else if (u.pathname === "/" || u.pathname === "") {
            navigate("/", { replace: true });
        } else {
            navigate(target, { replace: true });
        }
    } catch (e) {
        console.warn("[wyng] universal-link parse fail:", rawUrl, e);
    }
}
